/**
 * OCTOPUS Dashboard - FASE 3: Widget Library
 * GaugeRadial - Medidor circular para RPM, temperatura, pressão, velocidade
 * 
 * Estilo: Marítimo/Industrial moderno, tema dark
 */

import React, { useMemo } from 'react';

const GaugeRadial = ({
  value = 0,
  min = 0,
  max = 100,
  unit = '',
  label = '',
  zones = [],
  decimalPlaces = 1,
  showLabel = true,
  showUnit = true,
  size = 200,
  startAngle = -135,
  endAngle = 135,
}) => {
  // Calcular ângulo do ponteiro baseado no valor
  const angle = useMemo(() => {
    const clampedValue = Math.max(min, Math.min(max, value));
    const range = max - min;
    const valueRatio = (clampedValue - min) / range;
    return startAngle + valueRatio * (endAngle - startAngle);
  }, [value, min, max, startAngle, endAngle]);

  // Encontrar a cor da zona atual
  const currentZoneColor = useMemo(() => {
    if (!zones || zones.length === 0) return '#22c55e';
    const zone = zones.find(z => value >= z.min_value && value <= z.max_value);
    return zone?.color || '#22c55e';
  }, [value, zones]);

  // Gerar arcos das zonas
  const zoneArcs = useMemo(() => {
    if (!zones || zones.length === 0) return [];
    
    const radius = size * 0.38;
    const centerX = size / 2;
    const centerY = size / 2;
    const arcWidth = size * 0.06;
    
    return zones.map((zone, index) => {
      const startRatio = (zone.min_value - min) / (max - min);
      const endRatio = (zone.max_value - min) / (max - min);
      const zoneStartAngle = startAngle + startRatio * (endAngle - startAngle);
      const zoneEndAngle = startAngle + endRatio * (endAngle - startAngle);
      
      // Converter para radianos
      const startRad = (zoneStartAngle - 90) * Math.PI / 180;
      const endRad = (zoneEndAngle - 90) * Math.PI / 180;
      
      const x1 = centerX + radius * Math.cos(startRad);
      const y1 = centerY + radius * Math.sin(startRad);
      const x2 = centerX + radius * Math.cos(endRad);
      const y2 = centerY + radius * Math.sin(endRad);
      
      const largeArcFlag = (zoneEndAngle - zoneStartAngle) > 180 ? 1 : 0;
      
      return (
        <path
          key={index}
          d={`M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`}
          fill="none"
          stroke={zone.color}
          strokeWidth={arcWidth}
          strokeLinecap="round"
          opacity={0.7}
        />
      );
    });
  }, [zones, min, max, size, startAngle, endAngle]);

  // Gerar marcações da escala
  const ticks = useMemo(() => {
    const tickCount = 9;
    const radius = size * 0.32;
    const labelRadius = size * 0.24;
    const centerX = size / 2;
    const centerY = size / 2;
    
    const tickElements = [];
    
    for (let i = 0; i <= tickCount; i++) {
      const ratio = i / tickCount;
      const tickAngle = startAngle + ratio * (endAngle - startAngle);
      const rad = (tickAngle - 90) * Math.PI / 180;
      
      const x1 = centerX + (radius - 5) * Math.cos(rad);
      const y1 = centerY + (radius - 5) * Math.sin(rad);
      const x2 = centerX + radius * Math.cos(rad);
      const y2 = centerY + radius * Math.sin(rad);
      
      // Label da escala
      const tickValue = min + ratio * (max - min);
      const labelX = centerX + labelRadius * Math.cos(rad);
      const labelY = centerY + labelRadius * Math.sin(rad);
      
      tickElements.push(
        <g key={i}>
          <line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="#6b7280"
            strokeWidth={i % 2 === 0 ? 2 : 1}
          />
          {i % 2 === 0 && (
            <text
              x={labelX}
              y={labelY}
              fill="#9ca3af"
              fontSize={size * 0.055}
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {Math.round(tickValue)}
            </text>
          )}
        </g>
      );
    }
    
    return tickElements;
  }, [min, max, size, startAngle, endAngle]);

  // Ponteiro
  const pointer = useMemo(() => {
    const centerX = size / 2;
    const centerY = size / 2;
    const pointerLength = size * 0.28;
    const rad = (angle - 90) * Math.PI / 180;
    
    const tipX = centerX + pointerLength * Math.cos(rad);
    const tipY = centerY + pointerLength * Math.sin(rad);
    
    // Base do ponteiro (triângulo)
    const baseWidth = size * 0.04;
    const perpRad = rad + Math.PI / 2;
    const base1X = centerX + baseWidth * Math.cos(perpRad);
    const base1Y = centerY + baseWidth * Math.sin(perpRad);
    const base2X = centerX - baseWidth * Math.cos(perpRad);
    const base2Y = centerY - baseWidth * Math.sin(perpRad);
    
    return (
      <polygon
        points={`${tipX},${tipY} ${base1X},${base1Y} ${base2X},${base2Y}`}
        fill={currentZoneColor}
        filter="drop-shadow(0 2px 4px rgba(0,0,0,0.5))"
      />
    );
  }, [angle, size, currentZoneColor]);

  const formattedValue = typeof value === 'number'
    ? value.toFixed(decimalPlaces)
    : '--';

  // Painel atrás do valor: nos extremos da escala (±135°) a ponta do ponteiro
  // alcança a área do texto — o fundo sólido garante leitura sem obstrução
  const valueFontSize = size * 0.14;
  const unitFontSize = size * 0.07;
  const chipWidth = Math.max(
    formattedValue.length * valueFontSize * 0.62,
    showUnit && unit ? unit.length * unitFontSize * 0.62 : 0
  ) + size * 0.05;
  const chipTop = size * 0.645;
  const chipHeight = showUnit && unit ? size * 0.215 : size * 0.15;

  return (
    <div className="flex flex-col items-center">
      {/* Label ACIMA do gauge */}
      {showLabel && label && (
        <div 
          className="text-gray-300 font-medium text-center mb-1 truncate"
          style={{ 
            fontSize: Math.max(12, size * 0.07),
            maxWidth: size * 1.1,
          }}
          title={label}
        >
          {label}
        </div>
      )}
      
      {/* Gauge */}
      <div 
        className="relative flex items-center justify-center"
        style={{ width: size, height: size }}
      >
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {/* Fundo do gauge */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={size * 0.42}
            fill="#1f2937"
            stroke="#374151"
            strokeWidth={2}
          />
          
          {/* Círculo interno */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={size * 0.15}
            fill="#111827"
            stroke="#374151"
            strokeWidth={1}
          />
          
          {/* Arcos das zonas */}
          {zoneArcs}
          
          {/* Marcações da escala */}
          {ticks}
          
          {/* Ponteiro */}
          {pointer}
          
          {/* Centro do ponteiro */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={size * 0.04}
            fill="#374151"
            stroke={currentZoneColor}
            strokeWidth={2}
          />
          
          {/* Painel do valor — desenhado DEPOIS do ponteiro para cobri-lo nos extremos */}
          <rect
            x={size / 2 - chipWidth / 2}
            y={chipTop}
            width={chipWidth}
            height={chipHeight}
            rx={size * 0.025}
            fill="#111827"
            stroke="#374151"
            strokeWidth={1}
          />

          {/* Valor digital DENTRO do SVG, abaixo do centro — sobre o painel */}
          <text
            x={size / 2}
            y={size * 0.72}
            fill={currentZoneColor}
            fontSize={valueFontSize}
            fontWeight="bold"
            fontFamily="monospace"
            textAnchor="middle"
            dominantBaseline="middle"
            data-testid="gauge-radial-value"
          >
            {formattedValue}
          </text>
          {showUnit && unit && (
            <text
              x={size / 2}
              y={size * 0.82}
              fill="#9ca3af"
              fontSize={unitFontSize}
              fontWeight="500"
              textAnchor="middle"
              dominantBaseline="middle"
              data-testid="gauge-radial-unit"
            >
              {unit}
            </text>
          )}
        </svg>
      </div>
    </div>
  );
};

export default GaugeRadial;
